/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useRef, useState } from "react";
import { Animated, StyleProp, View, ViewStyle } from "react-native";

import { Text } from "UpduoLibrary";
import { upduoPrimaryBlue } from "config/UpduoColors";
import profilePicPlaceholder from "assets/profile-pic-placeholder.png";
import { UserType } from "graphql/internal/generated";
import { getResizedImageUrl } from "config/Utilities";

type ProfileImageProps = {
  style?: StyleProp<ViewStyle>;
  userObject?: Partial<
    Pick<
      UserType,
      "profileImgLgUrl" | "profileImgSmUrl" | "firstName" | "lastName"
    >
  >;
  size: number;
  round?: boolean;
  borderRadius?: number;
};

export default function ProfileImage({
  size,
  round = true,
  style,
  userObject,
  borderRadius,
}: ProfileImageProps) {
  const [thumbnailOpacity, setThumbnailOpacity] = useState(0);

  const animation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(animation, {
      toValue: thumbnailOpacity,
      duration: 250,
      useNativeDriver: true,
    }).start();
  }, [thumbnailOpacity, animation]);

  if (!userObject) {
    return null;
  }

  const thumbnailContainerStyle = {
    opacity: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
  };

  const imageStyle = {
    width: size,
    height: size,
    borderRadius: round ? size / 2 : borderRadius ? borderRadius : undefined,
  };

  const placeholder =
    userObject?.firstName && userObject?.lastName ? (
      <View
        style={[
          imageStyle,
          {
            backgroundColor: upduoPrimaryBlue,
            alignItems: "center",
            justifyContent: "center",
          },
        ]}
      >
        <Text
          style={{
            color: "white",
            fontSize: size / 2.5,
          }}
        >
          {(userObject.firstName?.[0]?.toUpperCase() || "") +
            (userObject.lastName?.[0]?.toUpperCase() || "")}
        </Text>
      </View>
    ) : (
      <Animated.Image style={imageStyle} source={profilePicPlaceholder} />
    );

  const uri =
    size > 100
      ? userObject?.profileImgLgUrl || userObject?.profileImgSmUrl
      : userObject?.profileImgSmUrl || userObject?.profileImgLgUrl;

  return (
    <View style={style}>
      {placeholder}
      {uri ? (
        <Animated.Image
          style={[
            thumbnailContainerStyle,
            { position: "absolute", top: 0, left: 0 },
            imageStyle,
          ]}
          source={{ uri: getResizedImageUrl(uri, size, size) }}
          onLoad={() => {
            setThumbnailOpacity(1);
          }}
        />
      ) : null}
    </View>
  );
}
